import { useState, useEffect, useRef, useCallback, type FormEvent } from 'react';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import EnrollmentGallery from '../components/users/EnrollmentGallery';
import EnrollmentModal, { type EnrollmentModalState, type EnrollmentResult } from '../components/users/EnrollmentModal';
import ConfirmModal from '../components/shared/ConfirmModal';
import GroupsTab from '../components/admin/GroupsTab';
import type { User, EnrollmentPhoto, Role, ApiErrorBody } from '../types';

const ROLES: { value: Role; label: string }[] = [
  { value: 'artist', label: 'Artist' },
  { value: 'artistic_director', label: 'Artistic Staff' },
  { value: 'admin', label: 'Admin' },
];

type CreateForm = { fName: string; lName: string; email: string; password: string; role: Role };
type EditForm = { fName: string; lName: string; email: string; role: Role };

const emptyForm: CreateForm = {
  fName: '',
  lName: '',
  email: '',
  password: '',
  role: 'artist',
};
const emptyEditForm: EditForm = { fName: '', lName: '', email: '', role: 'artist' };
const emptyModalState: EnrollmentModalState = {
  running: false,
  done: false,
  total: 0,
  currentIndex: 0,
  currentItem: null,
  currentStage: '',
  results: [],
};

type EnrollmentItem =
  | { type: 'remove'; id: number; photo: EnrollmentPhoto | undefined }
  | { type: 'add'; file: File };

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [fetchState, setFetchState] = useState<{ status: 'loading' | 'idle' | 'error'; error: string | null }>({
    status: 'loading',
    error: null,
  });
  const [roleFilter, setRoleFilter] = useState('');
  const [groupTab, setGroupTab] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditForm>(emptyEditForm);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

const [deactivating, setDeactivating] = useState<number | null>(null);
const [reactivating, setReactivating] = useState<number | null>(null);

type PendingAction = { type: 'deactivate' | 'reactivate'; userId: number; userName: string } | null;
const [pendingAction, setPendingAction] = useState<PendingAction>(null);
const [actionError, setActionError] = useState<string | null>(null);

  // Reset-password state
  const [resetPwUser, setResetPwUser] = useState<{ id: number; name: string } | null>(null);
  const [resetPwInput, setResetPwInput] = useState('');
  const [resetPwError, setResetPwError] = useState<string | null>(null);
  const [resetPwSaving, setResetPwSaving] = useState(false);

  // Enrollment state
  const [existingPhotos, setExistingPhotos] = useState<EnrollmentPhoto[]>([]);
  const [newPhotos, setNewPhotos] = useState<File[]>([]);
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const [localDevWarning, setLocalDevWarning] = useState(false);
  const [enrollModal, setEnrollModal] = useState<EnrollmentModalState | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const cancelRef = useRef(false);
  const editFormRef = useRef<HTMLFormElement | null>(null);

useEffect(() => {
  setFetchState({ status: 'loading', error: null });
  const url = roleFilter === 'deactivated'
    ? '/api/users?active=false'
    : roleFilter
      ? `/api/users?role=${roleFilter}`
      : '/api/users';
  apiFetch(url, { headers: authHeaders() })
    .then(r => r.json())
    .then((data: unknown) => {
      setUsers(Array.isArray(data) ? data : []);
      setFetchState({ status: 'idle', error: null });
    })
    .catch(err => {
      setFetchState({ status: 'error', error: err instanceof Error ? err.message : String(err) });
    });
}, [roleFilter, refresh]);

  const resetEnrollment = () => {
    setExistingPhotos([]);
    setNewPhotos([]);
    setRemovedIds(new Set());
    setLocalDevWarning(false);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiFetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      });
      const data: ApiErrorBody & { data?: { id: number } } = await res.json();
      if (!res.ok) {
        setSaveError(data.message || 'Failed to create user');
        return;
      }
      const userId = data.data!.id;
      if (newPhotos.length > 0) {
        await runEnrollment(userId);
      } else {
        setForm(emptyForm);
        setShowForm(false);
        resetEnrollment();
        setRefresh(n => n + 1);
      }
    } catch {
      setSaveError('Failed to create user');
    } finally {
      setSaving(false);
    }
  };

const handleDeactivate = (userId: number, userName: string) => {
  setPendingAction({ type: 'deactivate', userId, userName });
  setActionError(null);
};

const handleReactivate = (userId: number, userName: string) => {
  setPendingAction({ type: 'reactivate', userId, userName });
  setActionError(null);
};

const handleConfirmAction = async () => {
  if (!pendingAction) return;
  const { type, userId } = pendingAction;

  if (type === 'deactivate') {
    setDeactivating(userId);
    try {
      const res = await apiFetch(`/api/users/${userId}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) {
        const data: ApiErrorBody = await res.json();
        setActionError(data.message || 'Failed to deactivate user');
        return;
      }
      setUsers(prev => prev.filter(u => u.id !== userId));
      setPendingAction(null);
    } catch {
      setActionError('Failed to deactivate user');
    } finally {
      setDeactivating(null);
    }
  } else {
    setReactivating(userId);
    try {
      const res = await apiFetch(`/api/users/${userId}/reactivate`, { method: 'PATCH', headers: authHeaders() });
      if (!res.ok) {
        const data: ApiErrorBody = await res.json();
        setActionError(data.message || 'Failed to reactivate user');
        return;
      }
      setUsers(prev => prev.filter(u => u.id !== userId));
      setPendingAction(null);
    } catch {
      setActionError('Failed to reactivate user');
    } finally {
      setReactivating(null);
    }
  }
};

  const handleResetPassword = (userId: number, userName: string) => {
    setResetPwUser({ id: userId, name: userName });
    setResetPwInput('');
    setResetPwError(null);
  };

  const handleConfirmResetPassword = async () => {
    if (!resetPwUser) return;
    if (!resetPwInput || resetPwInput.length < 8) {
      setResetPwError('Password must be at least 8 characters.');
      return;
    }
    setResetPwSaving(true);
    setResetPwError(null);
    try {
      const res = await apiFetch(`/api/users/${resetPwUser.id}/reset-password`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword: resetPwInput }),
      });
      const data = await res.json() as { message?: string };
      if (!res.ok) {
        setResetPwError(data.message || 'Failed to reset password.');
        return;
      }
      setResetPwUser(null);
      setResetPwInput('');
    } catch {
      setResetPwError('Failed to reset password.');
    } finally {
      setResetPwSaving(false);
    }
  };

  const handleEdit = async (user: User) => {
    setEditingUser(user);
    setEditForm({
      fName: user.fName,
      lName: user.lName,
      email: user.email,
      role: user.role,
    });
    setEditError(null);
    setShowForm(false);
    setForm(emptyForm);
    resetEnrollment();
    setTimeout(() => editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);

    try {
      const res = await apiFetch(`/api/users/${user.id}/enrollment`, {
        headers: authHeaders(),
      });
      if (res.status === 503) {
        setLocalDevWarning(true);
      } else if (res.ok) {
        const data: EnrollmentPhoto[] = await res.json();
        setExistingPhotos(data);
      }
    } catch {
      // Non-fatal — gallery just stays empty
    }
  };

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError(null);
    try {
      const patchBody = editingUser!.role === 'admin'
        ? { fName: editForm.fName, lName: editForm.lName, email: editForm.email }
        : editForm;
      const res = await apiFetch(`/api/users/${editingUser!.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(patchBody),
      });
      const data: ApiErrorBody & { data?: User } = await res.json();
      if (!res.ok) {
        setEditError(data.message || 'Failed to update user');
        return;
      }
      setUsers(prev =>
        prev.map(u => u.id === editingUser!.id ? { ...data.data!, groups: u.groups } : u)
      );

      if (newPhotos.length > 0 || removedIds.size > 0) {
        await runEnrollment(editingUser!.id);
      } else {
        setEditingUser(null);
        setEditForm(emptyEditForm);
        resetEnrollment();
      }
    } catch {
      setEditError('Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  const runEnrollment = useCallback(
    async (userId: number) => {
      cancelRef.current = false;

      const removalItems: EnrollmentItem[] = [...removedIds].map(id => ({
        type: 'remove',
        id,
        photo: existingPhotos.find(p => p.id === id),
      }));
      const addItems: EnrollmentItem[] = newPhotos.map(file => ({ type: 'add', file }));
      const items: EnrollmentItem[] = [...removalItems, ...addItems];

      setEnrollModal({
        ...emptyModalState,
        running: true,
        total: items.length,
        results: [],
      });

      const itemFilename = (item: EnrollmentItem) =>
        item.type === 'add' ? item.file.name : `Photo #${item.id}`;

      const addResult = (item: EnrollmentItem, status: EnrollmentResult['status'], error?: string) => {
        const filename = itemFilename(item);
        setEnrollModal(prev => prev && ({
          ...prev,
          results: [
            ...prev.results,
            { filename, type: item.type, status, error },
          ],
        }));
      };

      for (let i = 0; i < items.length; i++) {
        if (cancelRef.current) {
          const remaining = items.slice(i);
          remaining.forEach(item => {
            const filename = itemFilename(item);
            setEnrollModal(prev => prev && ({
              ...prev,
              results: [
                ...prev.results,
                { filename, type: item.type, status: 'skipped' },
              ],
            }));
          });
          break;
        }

        const item = items[i];
        const preview =
          item.type === 'add'
            ? URL.createObjectURL(item.file)
            : (item.photo?.thumbnailUrl ?? '');
        const filename = itemFilename(item);

        setEnrollModal(prev => prev && ({
          ...prev,
          currentIndex: i,
          currentItem: { type: item.type, preview, filename },
          currentStage: item.type === 'remove' ? 'Removing...' : 'Preparing...',
        }));

        if (item.type === 'remove') {
          try {
            const res = await apiFetch(
              `/api/users/${userId}/enrollment/${item.id}`,
              {
                method: 'DELETE',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword: resetPwInput }),
              }
            );
            if (!res.ok) throw new Error((await res.json()).message);
            addResult(item, 'success');
          } catch (err) {
            addResult(item, 'failed', err instanceof Error ? err.message : String(err));
          }
          continue;
        }

        // type === 'add'
        let enrollmentPhotoId: number | undefined;
        try {
          // Step 1: reserve a temp key and get a presigned PUT URL
          const prepRes = await apiFetch(
            `/api/users/${userId}/enrollment/prepare`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ filename: item.file.name, size: item.file.size }),
            }
          );
          if (!prepRes.ok) throw new Error((await prepRes.json()).message);
          const { uploadUrl, tempKey } = await prepRes.json();

          // Step 2: PUT the binary directly to R2
          setEnrollModal(prev => prev && ({ ...prev, currentStage: 'Uploading...' }));
          const putRes = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'image/jpeg' },
            body: item.file,
          });
          if (!putRes.ok) throw new Error('Upload to storage failed');

          // Step 3: confirm — Worker resizes variants and inserts the DB row
          setEnrollModal(prev => prev && ({ ...prev, currentStage: 'Processing...' }));
          const confirmRes = await apiFetch(
            `/api/users/${userId}/enrollment/confirm`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ tempKey }),
            }
          );
          if (!confirmRes.ok) throw new Error((await confirmRes.json()).message);
          ({ id: enrollmentPhotoId } = await confirmRes.json());

          setEnrollModal(prev => prev && ({
            ...prev,
            currentStage: 'Indexing faces...',
          }));
          const indexRes = await apiFetch(
            `/api/users/${userId}/enrollment/${enrollmentPhotoId}/index`,
            {
              method: 'POST',
              headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword: resetPwInput }),
            }
          );
          if (!indexRes.ok) throw new Error((await indexRes.json()).message);
          const { faceId } = await indexRes.json();

          setEnrollModal(prev => prev && ({ ...prev, currentStage: 'Associating...' }));
          const assocRes = await apiFetch(
            `/api/users/${userId}/enrollment/${enrollmentPhotoId}/associate`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ faceId }),
            }
          );
          if (!assocRes.ok) throw new Error((await assocRes.json()).message);

          addResult(item, 'success');
        } catch (err) {
          if (enrollmentPhotoId) {
            apiFetch(`/api/users/${userId}/enrollment/${enrollmentPhotoId}`, {
              method: 'DELETE',
              headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword: resetPwInput }),
            }).catch(() => {});
          }
          addResult(item, 'failed', err instanceof Error ? err.message : String(err));
        }
      }

      setEnrollModal(prev => prev && ({
        ...prev,
        running: false,
        done: true,
        currentStage: 'Done',
      }));
    },
    [existingPhotos, newPhotos, removedIds]
  );

  const handleModalClose = () => {
    setEnrollModal(null);
    setShowCancelConfirm(false);
    setForm(emptyForm);
    setShowForm(false);
    setEditingUser(null);
    setEditForm(emptyEditForm);
    resetEnrollment();
    setRefresh(n => n + 1);
  };

  const fmtRole = (role: Role) => {
    if (role === 'artistic_director') return 'Artistic Staff';
    if (role === 'admin') return 'Admin';
    return 'Artist';
  };

  return (
    <div>
      <div className='user-mgmt-header'>
        <div className='user-filter-tabs'>
          {[
            { value: '', label: 'All' },
            { value: 'artist', label: 'Artists' },
            { value: 'artistic_director', label: 'Artistic Staff' },
            { value: 'admin', label: 'Admins' },
            { value: 'deactivated', label: 'Deactivated' },
          ].map(f => (
            <button
              key={f.value}
              className={`user-filter-btn${roleFilter === f.value && !groupTab ? ' active' : ''}`}
              onClick={() => { setRoleFilter(f.value); setGroupTab(false); }}
            >
              {f.label}
            </button>
          ))}
          <button
            className={`user-filter-btn${groupTab ? ' active' : ''}`}
            onClick={() => { setGroupTab(true); setRoleFilter(''); setShowForm(false); setEditingUser(null); }}
          >
            Groups
          </button>
        </div>
        {!groupTab && !showForm && !editingUser && (
          <button
            className='user-add-btn'
            onClick={() => {
              setShowForm(true);
              setSaveError(null);
              setEditingUser(null);
              setEditForm(emptyEditForm);
              resetEnrollment();
            }}
          >
            + Add User
          </button>
        )}
      </div>

      {groupTab ? (
        <GroupsTab />
      ) : (
        <>
      {showForm && (
        <form className='user-form' onSubmit={handleCreate}>
          <h3 className='user-form-title'>New User</h3>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>First Name</label>
              <input
                value={form.fName}
                onChange={e => setForm(f => ({ ...f, fName: e.target.value }))}
                required
              />
            </div>
            <div className='user-form-field'>
              <label>Last Name</label>
              <input
                value={form.lName}
                onChange={e => setForm(f => ({ ...f, lName: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>Email</label>
              <input
                type='email'
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
              />
            </div>
            <div className='user-form-field'>
              <label>Temporary Password</label>
              <input
                type='text'
                value={form.password}
                onChange={e =>
                  setForm(f => ({ ...f, password: e.target.value }))
                }
                required
                minLength={8}
              />
            </div>
          </div>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>Role</label>
              <select
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value as Role }))}
              >
                {ROLES.map(r => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {form.role === 'artist' && (
            <EnrollmentGallery
              existingPhotos={existingPhotos}
              newPhotos={newPhotos}
              removedIds={removedIds}
              onAdd={files => setNewPhotos(prev => [...prev, ...files])}
              onRemoveNew={i =>
                setNewPhotos(prev => prev.filter((_, idx) => idx !== i))
              }
              onRemoveExisting={id =>
                setRemovedIds(prev => new Set([...prev, id]))
              }
              localDevWarning={localDevWarning}
            />
          )}
          {saveError && <p className='user-form-error'>{saveError}</p>}
          <div className='user-form-actions'>
            <button
              type='button'
              className='user-cancel-btn'
              onClick={() => {
                setShowForm(false);
                setForm(emptyForm);
                setSaveError(null);
                resetEnrollment();
              }}
            >
              Cancel
            </button>
            <button type='submit' className='user-submit-btn' disabled={saving}>
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      )}

      {editingUser && (
        <form className='user-form' ref={editFormRef} onSubmit={handleUpdate}>
          <h3 className='user-form-title'>Edit User</h3>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>First Name</label>
              <input
                value={editForm.fName}
                onChange={e =>
                  setEditForm(f => ({ ...f, fName: e.target.value }))
                }
                required
              />
            </div>
            <div className='user-form-field'>
              <label>Last Name</label>
              <input
                value={editForm.lName}
                onChange={e =>
                  setEditForm(f => ({ ...f, lName: e.target.value }))
                }
                required
              />
            </div>
          </div>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>Email</label>
              <input
                type='email'
                value={editForm.email}
                onChange={e =>
                  setEditForm(f => ({ ...f, email: e.target.value }))
                }
                required
              />
            </div>
            <div className='user-form-field'>
              <label>Role</label>
              {editingUser?.role === 'admin' ? (
                <input value='Admin' disabled readOnly />
              ) : (
                <select
                  value={editForm.role}
                  onChange={e =>
                    setEditForm(f => ({ ...f, role: e.target.value as Role }))
                  }
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          {editForm.role === 'artist' && editingUser?.role !== 'admin' && (
            <EnrollmentGallery
              existingPhotos={existingPhotos}
              newPhotos={newPhotos}
              removedIds={removedIds}
              onAdd={files => setNewPhotos(prev => [...prev, ...files])}
              onRemoveNew={i =>
                setNewPhotos(prev => prev.filter((_, idx) => idx !== i))
              }
              onRemoveExisting={id =>
                setRemovedIds(prev => new Set([...prev, id]))
              }
              localDevWarning={localDevWarning}
            />
          )}
          {editError && <p className='user-form-error'>{editError}</p>}
          <div className='user-form-actions'>
            <button
              type='button'
              className='user-cancel-btn'
              onClick={() => {
                setEditingUser(null);
                setEditForm(emptyEditForm);
                setEditError(null);
                resetEnrollment();
              }}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='user-submit-btn'
              disabled={editSaving}
            >
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}

      {fetchState.status === 'loading' && (
        <p className='user-loading'>Loading users...</p>
      )}
      {fetchState.status === 'error' && (
        <p className='user-error'>Failed to load users: {fetchState.error}</p>
      )}
      {fetchState.status === 'idle' && users.length === 0 && (
        <p className='user-empty'>
          {roleFilter === 'deactivated' ? 'No deactivated users.' : 'No users found.'}
        </p>
      )}
      {fetchState.status === 'idle' && users.length > 0 && (
      <div className='table-responsive'>
        <table className='user-table'>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Groups</th>
              <th className='enrolled-cell'>Auto Tag with AI</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>
                  {u.fName} {u.lName}
                </td>
                <td>{u.email}</td>
                <td>
                  <span className={`role-badge role-${u.role}`}>
                    {fmtRole(u.role)}
                  </span>
                </td>
                <td>
                  {u.role === 'artist' && u.groups && u.groups.length > 0 && (
                    <div className='user-group-chips'>
                      {u.groups.slice(0, 2).map(g => (
                        <span key={g} className='user-group-chip'>{g}</span>
                      ))}
                      {u.groups.length > 2 && (
                        <span className='user-group-chip user-group-chip--more'>
                          +{u.groups.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className='enrolled-cell'>
                  {u.role === 'artist' &&
                    (u.isEnrolled ? (
                      <span className='enrolled-check'>&#10003;</span>
                    ) : (
                      <span className='enrolled-missing'>&#10007;</span>
                    ))}
                </td>
                <td>
                  {u.isActive ? (
                    <>
                      <button
                        className='user-edit-btn'
                        onClick={() => handleEdit(u)}
                        disabled={deactivating === u.id}
                      >
                        Edit
                      </button>
                      <button
                        className='user-reset-pw-btn'
                        onClick={() => handleResetPassword(u.id, `${u.fName} ${u.lName}`)}
                        disabled={deactivating === u.id}
                      >
                        Reset password
                      </button>
                      <button
                        className='user-deactivate-btn'
                        onClick={() => handleDeactivate(u.id, `${u.fName} ${u.lName}`)}
                        disabled={deactivating === u.id}
                      >
                        {deactivating === u.id ? 'Deactivating…' : 'Deactivate'}
                      </button>
                    </>
                  ) : (
                    <button
                      className='user-reactivate-btn'
                      onClick={() => handleReactivate(u.id, `${u.fName} ${u.lName}`)}
                      disabled={reactivating === u.id}
                    >
                      {reactivating === u.id ? 'Reactivating…' : 'Reactivate'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

        </>
      )}

      {resetPwUser && (
        <ConfirmModal
          title='Reset Password'
          body={
            <>
              <p style={{ marginBottom: 12 }}>Set a new password for <strong>{resetPwUser.name}</strong>.</p>
              <div className='user-form-field'>
                <label htmlFor='reset-pw-input'>New password</label>
                <input
                  id='reset-pw-input'
                  type='password'
                  value={resetPwInput}
                  onChange={e => setResetPwInput(e.target.value)}
                  autoComplete='new-password'
                  minLength={8}
                  placeholder='Minimum 8 characters'
                />
              </div>
              {resetPwError && <p className='user-form-error' style={{ marginTop: 12, marginBottom: 0 }}>{resetPwError}</p>}
            </>
          }
          confirmLabel={resetPwSaving ? 'Saving…' : 'Set password'}
          isDestructive={false}
          onConfirm={handleConfirmResetPassword}
          onCancel={() => { setResetPwUser(null); setResetPwInput(''); setResetPwError(null); }}
        />
      )}

      {pendingAction && (
        <ConfirmModal
          title={pendingAction.type === 'deactivate' ? 'Deactivate User' : 'Reactivate User'}
          body={
            <>
              {pendingAction.type === 'deactivate'
                ? <><strong>{pendingAction.userName}</strong> will no longer be able to log in.</>
                : <><strong>{pendingAction.userName}</strong> will be able to log in again.</>
              }
              {actionError && <p className='user-form-error' style={{ marginTop: '12px', marginBottom: 0 }}>{actionError}</p>}
            </>
          }
          confirmLabel={pendingAction.type === 'deactivate' ? 'Deactivate' : 'Reactivate'}
          isDestructive={pendingAction.type === 'deactivate'}
          onConfirm={handleConfirmAction}
          onCancel={() => { setPendingAction(null); setActionError(null); }}
        />
      )}

      {enrollModal && (
        <EnrollmentModal
          state={enrollModal}
          showCancelConfirm={showCancelConfirm}
          onCancelClick={() => setShowCancelConfirm(true)}
          onCancelConfirm={() => {
            cancelRef.current = true;
            setShowCancelConfirm(false);
          }}
          onCancelDeny={() => setShowCancelConfirm(false)}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}
